/* eslint-disable @typescript-eslint/naming-convention */
import { SizeUnitType } from '@/utils/unit-kit';
import { CanvasType, PosterType } from '@/version-types';
import { shouldStopMouseEvent, type IService } from '../../shared';
import { EventGenerator } from '../events';
import type { ServiceManager } from '../service-manager';

const __contextMenuEventName: string = 'viewport:editor:1';
const __mouseMoveEventName: string = 'viewport:editor:2';
const __mouseDownEventName: string = 'viewport:editor:3';
const __mouseUpEventName: string = 'viewport:editor:4';
const __wheelEventName: string = 'viewport:editor:5';
const __mouseEnterEventName: string = 'viewport:editor:6';
const __mouseLeaveEventName: string = 'viewport:editor:7';
// const __dragOverLayerEventName: string = 'viewport:editor:8';
// const __dragLeaveLayerEventName: string = 'viewport:editor:9';
const __dropLayerEventName: string = 'viewport:editor:10';

export class EditorService implements IService {
    /** 编辑器容器 */
    private __container: HTMLElement | undefined;
    private __resizeObserver: ResizeObserver | undefined;
    private __containerWindow: Window | null = null;
    private __clientRectDirty = true;
    private __invalidateClientRect = () => {
        this.__clientRectDirty = true;
    };
    /** 编辑器容器矩形 */
    private __clientRect: {
        left: number;
        top: number;
        width: number;
        height: number;
        bottom: number;
        right: number;
        x: number;
        y: number;
    };

    constructor(readonly service: ServiceManager) {
        const commonEvent = {
            eventName: 'onStateChange',
            fetchEventArgs: () => [
                {
                    disableOperate: this.disableOperate,
                    disablePick: this.disablePick,
                    disableMouseSelect: this.disableMouseSelect,
                    disableMove: this.disableMove,
                    disableScale: this.disableScale,
                    disableRotate: this.disableRotate,
                    disableResize: this.disableResize,
                    disableActive: this.disableActive,
                    disableHover: this.disableHover,
                    isBaseOperate: this.isBaseOperate,
                    isMouseSelect: this.isMouseSelect,
                },
            ],
        };
        const eventGenerator = new EventGenerator('editor', this, service, {
            disable: commonEvent,
            is: commonEvent,
        });
        console.log('editor attrs ==>', eventGenerator.attrs);
        this.__clientRect = { left: 0, top: 0, width: 0, height: 0, bottom: 0, right: 0, x: 0, y: 0 };
    }

    onInit() {
        this.setContextMenuVisible(false);
        this.setDisableOperate(false);
        this.setDisablePick(false);
        this.setDisableMouseSelect(false);
        this.setDisableMove(false);
        this.setDisableScale(false);
        this.setDisableRotate(false);
        this.setDisableResize(false);
        this.setDisableActive(false);
        this.setDisableHover(false);
        this.setIsBaseOperate(false);
        this.setIsMouseSelect(false);
    }

    onBindEvent() {
        this.service.edgelessCanvas.onSizeChange(this.__invalidateClientRect, 99999);
        // 监听编辑器只读状态变化
        this.onReadonlyChange((readonly) => {
            this.setDisableActive(readonly);
            this.setDisableMouseSelect(readonly);
            this.setDisableAllBaseOperate(readonly);
        });
    }

    /** 卸载时释放布局监听，重新挂载后按需绑定新容器 */
    onUnmount() {
        this.__resizeObserver?.disconnect();
        this.__resizeObserver = undefined;
        this.__containerWindow?.removeEventListener('resize', this.__invalidateClientRect);
        this.__containerWindow?.removeEventListener('scroll', this.__invalidateClientRect, true);
        this.__containerWindow = null;
        this.__container = undefined;
        this.__invalidateClientRect();
    }

    /** 监听编辑器状态 */
    declare onStateChange: (
        fn: (state: {
            disableOperate: boolean;
            disablePick: boolean;
            disableMouseSelect: boolean;
            disableMove: boolean;
            disableScale: boolean;
            disableRotate: boolean;
            disableResize: boolean;
            disableActive: boolean;
            disableHover: boolean;
            isBaseOperate: boolean;
            isMouseSelect: boolean;
        }) => void,
    ) => number;

    /** 编辑器只读 */
    readonly readonly: boolean = false;
    declare getReadonly: () => boolean;
    declare setReadonly: (readonly: boolean) => void;
    declare onReadonlyChange: (fn: (readonly: boolean) => void, widget?: number) => number;

    /** 用户唯一标识 */
    readonly uid: number = 0;
    declare getUid: () => number;
    declare setUid: (uid: number) => void;
    declare onUidChange: (fn: (uid: number) => void) => number;

    /** 主题 */
    readonly theme: string = '';
    declare getTheme: () => string;
    declare setTheme: (theme: string) => void;
    declare onThemeChange: (fn: (theme: string) => void, widget?: number) => number;

    /** 单位 */
    readonly unit: SizeUnitType = SizeUnitType.px;
    declare getUnit: () => SizeUnitType;
    declare setUnit: (unit: SizeUnitType) => void;
    declare onUnitChange: (fn: (unit: SizeUnitType) => void, widget?: number) => number;

    /** 海报类型/编辑器类型 */
    readonly posterType: PosterType = PosterType.Poster;
    declare getPosterType: () => PosterType;
    declare setPosterType: (posterType: PosterType) => void;
    declare onPosterTypeChange: (fn: (posterType: PosterType) => void, widget?: number) => number;

    /** 接口下发的模版模式 */
    readonly canvasType: CanvasType = CanvasType.NORMAL;
    declare getCanvasType: () => CanvasType;
    declare setCanvasType: (canvasType: CanvasType) => void;
    declare onCanvasTypeChange: (fn: (canvasType: CanvasType) => void, widget?: number) => number;

    /** 右键菜单可见性 */
    readonly contextMenuVisible: boolean = false;
    declare getContextMenuVisible: () => boolean;
    declare setContextMenuVisible: (contextMenuVisible: boolean) => void;

    /** 是否禁用编辑器操作 */
    readonly disableOperate: boolean = false;
    declare getDisableOperate: () => boolean;
    declare setDisableOperate: (disableOperate: boolean) => void;
    declare onDisableOperateChange: (fn: (disableOperate: boolean) => void, widget?: number) => number;

    /** 触发禁用图层选择的操作 */
    readonly disablePick: boolean = false;
    declare getDisablePick: () => boolean;
    declare setDisablePick: (disablePick: boolean) => void;
    declare onDisablePickChange: (fn: (disablePick: boolean) => void, widget?: number) => number;

    /** 是否正在执行鼠标框选 */
    readonly disableMouseSelect: boolean = false;
    declare getDisableMouseSelect: () => boolean;
    declare setDisableMouseSelect: (disableMouseSelect: boolean) => void;
    declare onDisableMouseSelectChange: (fn: (disableMouseSelect: boolean) => void, widget?: number) => number;

    /** 是否禁用移动 */
    readonly disableMove: boolean = false;
    declare getDisableMove: () => boolean;
    declare setDisableMove: (disableMove: boolean) => void;
    declare onDisableMoveChange: (fn: (disableMove: boolean) => void, widget?: number) => number;

    /** 是否禁用缩放 */
    readonly disableScale: boolean = false;
    declare getDisableScale: () => boolean;
    declare setDisableScale: (disableScale: boolean) => void;
    declare onDisableScaleChange: (fn: (disableScale: boolean) => void, widget?: number) => number;

    /** 是否禁用旋转 */
    readonly disableRotate: boolean = false;
    declare getDisableRotate: () => boolean;
    declare setDisableRotate: (disableRotate: boolean) => void;
    declare onDisableRotateChange: (fn: (disableRotate: boolean) => void, widget?: number) => number;

    /** 是否禁用尺寸调整 */
    readonly disableResize: boolean = false;
    declare getDisableResize: () => boolean;
    declare setDisableResize: (disableResize: boolean) => void;
    declare onDisableResizeChange: (fn: (disableResize: boolean) => void, widget?: number) => number;

    /** 是否禁用激活图层 */
    readonly disableActive: boolean = false;
    declare getDisableActive: () => boolean;
    declare setDisableActive: (disableActive: boolean) => void;
    declare onDisableActiveChange: (fn: (disableActive: boolean) => void, widget?: number) => number;

    /** 是否禁用 Hover */
    readonly disableHover: boolean = false;
    declare getDisableHover: () => boolean;
    declare setDisableHover: (disableHover: boolean) => void;
    declare onDisableHoverChange: (fn: (disableHover: boolean) => void, widget?: number) => number;

    /** 是否正在执行基础操作：移动，旋转，缩放，尺寸 */
    readonly isBaseOperate: boolean = false;
    declare getIsBaseOperate: () => boolean;
    declare setIsBaseOperate: (isBaseOperate: boolean) => void;
    declare onIsBaseOperateChange: (fn: (isBaseOperate: boolean) => void, widget?: number) => number;

    /** 是否正在执行鼠标框选 */
    readonly isMouseSelect: boolean = false;
    declare getIsMouseSelect: () => boolean;
    declare setIsMouseSelect: (isMouseSelect: boolean) => void;
    declare onIsMouseSelectChange: (fn: (isMouseSelect: boolean) => void, widget?: number) => number;

    /** 设置禁用所有基础操作  */
    setDisableAllBaseOperate(disable: boolean) {
        this.setDisableMove(disable);
        this.setDisableRotate(disable);
        this.setDisableScale(disable);
        this.setDisableResize(disable);
    }

    /** 监听鼠标进入事件 */
    onMouseEnter(fn: (originIndex: number, force?: boolean) => void, weight = 0) {
        return this.service.events.on(__mouseEnterEventName, fn, weight);
    }

    /** 触发鼠标进入事件 */
    __emitMouseEnter(originIndex: number, force?: boolean) {
        this.service.events.emit(__mouseEnterEventName, originIndex, force);
    }

    /** 监听鼠标离开事件 */
    onMouseLeave(fn: (originIndex: number, force?: boolean) => void, weight = 0) {
        return this.service.events.on(__mouseLeaveEventName, fn, weight);
    }

    /** 触发鼠标离开事件 */
    __emitMouseLeave(originIndex: number, force?: boolean) {
        this.service.events.emit(__mouseLeaveEventName, originIndex, force);
    }

    /** 监听右键菜单事件 */
    onContextMenu(fn: (ev: React.MouseEvent) => void, weight = 0) {
        return this.service.events.on(__contextMenuEventName, fn, weight);
    }

    /** 触发右键菜单事件 */
    __emitContextMenuEvent(ev: React.MouseEvent) {
        this.service.events.emit(__contextMenuEventName, ev);
    }

    /** 监听鼠标移动事件 */
    onMouseMove(fn: (ev: React.MouseEvent) => void, weight = 0) {
        return this.service.events.on(__mouseMoveEventName, fn, weight);
    }

    /** 触发鼠标移动事件 */
    __emitMouseMoveEvent(ev: React.MouseEvent) {
        this.service.events.emit(__mouseMoveEventName, ev);
    }

    /** 监听鼠标按下事件 */
    onMouseDown(fn: (ev: React.MouseEvent) => void, weight = 0) {
        return this.service.events.on(__mouseDownEventName, fn, weight);
    }

    /** 触发鼠标按下事件 */
    __emitMouseDownEvent(ev: React.MouseEvent) {
        if (shouldStopMouseEvent(ev.target)) return;
        this.service.events.emit(__mouseDownEventName, ev);
    }

    /** 监听鼠标抬起事件 */
    onMouseUp(fn: (ev: React.MouseEvent) => void, weight = 0) {
        return this.service.events.on(__mouseUpEventName, fn, weight);
    }

    /** 触发鼠标抬起事件 */
    __emitMouseUpEvent(ev: React.MouseEvent) {
        if (shouldStopMouseEvent(ev.target)) return;
        this.service.events.emit(__mouseUpEventName, ev);
    }

    /** 监听滚轮事件 */
    onWheel(fn: (ev: React.WheelEvent) => void, weight = 0) {
        return this.service.events.on(__wheelEventName, fn, weight);
    }

    /** 触发滚轮事件 */
    __emitWheelEvent(ev: React.WheelEvent | WheelEvent) {
        this.service.events.emit(__wheelEventName, ev);
    }

    /** 监听图层拖拽放下事件 */
    onDropLayer(fn: (ev: React.DragEvent<HTMLDivElement>) => void, weight = 0) {
        return this.service.events.on(__dropLayerEventName, fn, weight);
    }

    /** 触发图层拖拽放下事件 */
    __emitDropLayerEvent(ev: React.DragEvent<HTMLDivElement>) {
        this.service.events.emit(__dropLayerEventName, ev);
    }

    /** 获取编辑器容器 */
    getContainer() {
        if (!this.__container) {
            const selector = `[data-editor-id="${this.service.identity}"]`;
            this.__container = document.querySelector(selector) as HTMLElement;
            if (this.__container) {
                this.__invalidateClientRect();
                this.__resizeObserver = new ResizeObserver(this.__invalidateClientRect);
                // 祖先尺寸变化也可能只移动容器，不改变容器自身的宽高。
                for (let element: HTMLElement | null = this.__container; element; element = element.parentElement) {
                    this.__resizeObserver.observe(element, { box: 'border-box' });
                }
                this.__containerWindow = this.__container.ownerDocument.defaultView;
                this.__containerWindow?.addEventListener('resize', this.__invalidateClientRect);
                this.__containerWindow?.addEventListener('scroll', this.__invalidateClientRect, true);
            }
        }
        return this.__container;
    }

    /**
     * 根据当前布局获取编辑器容器中心点
     *
     * @returns 容器中心点的视口坐标
     */
    getCenterPoint() {
        const { left, top, width, height } = this.getBoundingClientRect();

        return {
            x: left + width / 2,
            y: top + height / 2,
        };
    }

    /**
     * 根据当前布局获取编辑器容器起点
     *
     * @returns 容器左上角的视口坐标
     */
    getStartPoint() {
        const { left: x, top: y } = this.getBoundingClientRect();
        return { x, y };
    }

    /**
     * 读取编辑器容器边界缓存，供鼠标坐标换算和画布定位使用
     *
     * 布局或滚动变化只标记失效，在下次读取时测量，避免高频鼠标事件重复测量 DOM。
     *
     * @returns 当前布局中的容器边界
     */
    getBoundingClientRect() {
        this.getContainer();
        if (this.__clientRectDirty) this.__updateClientRect();
        return this.__clientRect;
    }

    /** 更新编辑器容器中心点和起点 */
    private __updateClientRect() {
        if (!this.__container) return;
        this.__clientRect = this.__container.getBoundingClientRect();
        this.__clientRectDirty = false;
    }
}
